import { useContext, useEffect, useState } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';

import i18next from 'i18next';

import { ProductType, TechnicalData } from 'types/api/product.type';

import { useAppSelector } from 'redux/hooks';
import { getUserPermissions } from 'redux/slices/userData';

import { ProductParams, productDataService } from 'services/products.service';

import { CatalogueContext, LayoutContext } from 'contexts';

import { useCatalogueLabels, useFetch, useScrollToTop } from 'hooks';

import { buildCategoryBreadcrumbSecondary } from 'helpers/buildCategoryBreadcrumb';
import { checkParams } from 'helpers/checkParams';
import { getSearchParams } from 'helpers/getSearchParams';

import { ListItem } from '../ListItem';

const useColorsList = (isGridChangeSet: Function) => {
  const { genre, category } = useParams();
  const { genreLabel, categoryLabel } = useCatalogueLabels();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const { breadcrumbSet, breadcrumbSecondarySet, additionalInfoSet } =
    useContext(LayoutContext);
  const { subProductsSet, subProducts } = useContext(CatalogueContext);
  const { season, marketLine, warehouse, zone } = useAppSelector((state) => state.settings);
  const permissions = useAppSelector(getUserPermissions);

  const [technicalData, technicalDataSet] = useState<TechnicalData>();
  const { language } = i18next;
  const colorsApiParams: ProductParams = {
    season,
    marketLine,
    genre,
    category,
    warehouse: warehouse.code,
    zone,
    ...getSearchParams(searchParams),
  };
  const technicalApiParams: ProductParams = {
    season,
    marketLine,
    genre,
    category,
    zone,
    warehouse: warehouse.code,
    withSize: 'true',
    ...getSearchParams(searchParams),
  };
  const { content, isLoading } = useFetch(
    checkParams(colorsApiParams)
      ? () => productDataService.getSubProducts(colorsApiParams)
      : undefined,
    [season, marketLine, genre, category, zone, warehouse]
  );
  const technicalDataFetch = useFetch(
    checkParams(technicalApiParams)
      ? () => productDataService.getTechnicalData(technicalApiParams)
      : undefined,
    [marketLine, genre, category, zone, warehouse]
  );

  const productComponents = subProducts?.map((item: ProductType) => {
    if (!permissions.includes('VIEW_INACTIVE_ITEMS') && !item.active) return null;

    return (
      <ListItem
        key={item.id}
        data={item}
        title={item.colorCode}
        desc={language === 'en' ? item.englishColorDescription : item.italianColorDescription}
        asSample={item.colorUsedInSample === 'true'}
        asSubProduct
      />
    );
  });

  useEffect(() => {
    if (!subProducts || subProducts.length === 0) return;
    const firstItem = subProducts[0];

    const breadcrumb = [
      {
        label: `${firstItem.genderCode} ${firstItem.itemCode} ${firstItem.mainFabricCode}`,
      },
    ];

    const additionalInfo = [
      language === 'en' ? firstItem.englishItemMainTitle : firstItem.italianItemMainTitle,
      `${firstItem.mainFabricCode} ${firstItem.mainFabricDesc}`,
      firstItem.styleCodeAndStyleDesc,
    ];

    breadcrumbSet(breadcrumb);
    additionalInfoSet(additionalInfo);
  }, [subProducts]);

  useEffect(() => {
    if (genre && season && marketLine && category) {
      breadcrumbSecondarySet(
        buildCategoryBreadcrumbSecondary(
          season,
          marketLine,
          genre,
          category,
          genreLabel(genre),
          categoryLabel(category)
        )
      );
    }
  }, [season, marketLine, genre, category, genreLabel, categoryLabel]);

  useEffect(() => {
    isGridChangeSet(false);
  }, []);

  useEffect(() => {
    if (technicalDataFetch.content) {
      technicalDataSet(technicalDataFetch.content);
    }
  }, [technicalDataFetch]);

  useEffect(() => {
    if (!content) return;

    if (!Array.isArray(content)) return;

    const seenIds = new Set<string>();
    const deduplicated = content.filter((item: ProductType) => {
      if (!item?.id) return true;
      if (seenIds.has(item.id)) return false;
      seenIds.add(item.id);
      return true;
    });
    subProductsSet(deduplicated);
  }, [content]);

  useScrollToTop(pathname);

  return {
    productComponents,
    isLoading,
    technicalData,
    language,
  };
};

export { useColorsList };
